import apiClient from "./client";
import type { ApiErrorResponse } from "./events";
import type { EventTemplateId } from "@/lib/event-templates";
import { API_ROUTES_RELATIVE } from "@/routes/api";

/**
 * Thrown by uploadGalleryImage/uploadHeroImage on a 4xx from the backend.
 * `code` is one of the ApiErrorCode values the backend can return for these
 * endpoints (see server/app/Enums/ApiErrorCode.php): invalid_file_type,
 * file_too_large, file_upload_failed (undecodable/corrupted content),
 * plan_limit_exceeded, validation_failed. Callers map `code` to a translated,
 * failure-specific message (JUB-84) instead of showing the raw axios error.
 */
export class GalleryUploadError extends Error {
  code: string;

  constructor(code: string, message?: string) {
    super(message ?? code);
    this.name = "GalleryUploadError";
    this.code = code;
  }
}

/** Maps a GalleryUploadError.code to an i18n key under events.form.* */
export function galleryUploadErrorMessageKey(code: string): string {
  switch (code) {
    case "invalid_file_type":
      return "events.form.galleryInvalidFormat";
    case "file_too_large":
      return "events.form.galleryMaxSize";
    case "plan_limit_exceeded":
      return "events.form.galleryLimitReached";
    // JUB-39: the event's total image storage is full (all upload types).
    case "event_storage_limit_reached":
      return "events.form.eventStorageLimitReached";
    case "event_media_busy":
      return "events.form.eventMediaBusy";
    case "event_media_conflict":
      return "events.form.eventMediaConflict";
    case "media_delete_failed":
      return "events.form.mediaDeleteFailed";
    case "plan_feature_locked":
      return "events.plan.giftsLocked";
    case "file_upload_failed":
    case "validation_failed":
    default:
      return "events.form.galleryUploadFailed";
  }
}

/** Extracts a GalleryUploadError from an axios error, falling back to the original error. */
export function toGalleryUploadError(error: unknown): Error {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { data?: { error?: { code?: string; message?: string } } } };
    const errData = axiosError.response?.data?.error;
    if (errData?.code) {
      return new GalleryUploadError(errData.code, errData.message);
    }
  }
  return error instanceof Error ? error : new Error("Upload failed");
}

export type EventCeremony = {
  name: string;
  /** JUB-154: explicit display title ("Cununia religioasă și Botezul"); wins over venue/fallback. */
  title?: string | null;
  enabled: boolean;
  venue?: string | null;
  address?: string | null;
  city?: string | null;
  datetime?: string | null;
  /** Free-text note shown under the ceremony (transport, dress code, ...). */
  notes?: string | null;
};

export type EventFamily = {
  parents?: string | null;
  godparents?: string | null;
};

export type EventLandingSettings = {
  theme: EventTemplateId;
  fontFamily?: string | null;
  fontScale?: "small" | "medium" | "large" | null;
  story?: string | null;
  heroText?: { ro?: string; en?: string } | null;
  gallery: string[];
  passwordEnabled: boolean;
  passwordSet?: boolean; // Indicates if a password is already set
  searchable: boolean;
  registryPublic: boolean;
  slug: string;
  /** When the event URL was last changed (for once-per-week cooldown). */
  urlChangedAt?: string | null;
  /** JUB-64: true once the event has ever gone live — the slug is frozen for good. */
  urlLocked?: boolean;
  showTimeline: boolean;
  showGallery: boolean;
  showStory: boolean;
  colorScheme?: string | null;
  family: EventFamily;
  ceremonies: {
    civil: EventCeremony;
    religious: EventCeremony;
    party: EventCeremony;
    /** JUB-154: separate baptism slot (wedding + baptism on different ceremonies). */
    baptism?: EventCeremony;
  };
};

export type EventLandingSettingsPayload = Partial<
  Omit<EventLandingSettings, "gallery" | "slug" | "family" | "ceremonies" | "showTimeline" | "showGallery" | "showStory" | "colorScheme" | "heroText">
> & {
  gallery?: string[];
  password?: string;
  url?: string;
  showTimeline?: boolean;
  showGallery?: boolean;
  showStory?: boolean;
  colorScheme?: string | null;
  heroText?: { ro?: string; en?: string } | null;
  family?: EventFamily;
  ceremonies?: {
    civil?: Partial<EventCeremony>;
    religious?: Partial<EventCeremony>;
    party?: Partial<EventCeremony>;
    baptism?: Partial<EventCeremony>;
  };
};

export type LandingDraftOverrides = Partial<Omit<EventLandingSettings, "ceremonies">> & {
  slug?: string;
  ceremonies?: Partial<Record<keyof EventLandingSettings["ceremonies"], EventCeremony>>;
};

/**
 * The owner's unsaved overrides carried in the preview link's `draft` param.
 * Not a credential (JUB-151): access comes from the server-signed `preview`
 * token, and the page applies these only when the API confirmed preview mode.
 */
export type LandingDraftPayload = {
  eventUuid: string;
  overrides?: LandingDraftOverrides;
};

export async function getEventLandingSettings(eventId: string): Promise<EventLandingSettings> {
  const response = await apiClient.get<EventLandingSettings | ApiErrorResponse>(
    `/events/${eventId}/landing-settings`,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to load landing settings");
  }

  return data as EventLandingSettings;
}

export async function updateEventLandingSettings(
  eventId: string,
  payload: EventLandingSettingsPayload,
): Promise<EventLandingSettings> {
  const response = await apiClient.put<EventLandingSettings | ApiErrorResponse>(
    `/events/${eventId}/landing-settings`,
    payload,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to update landing settings");
  }

  return data as EventLandingSettings;
}

/**
 * JUB-151: owner-only, server-signed token that opens the owner's preview of
 * the public page (unpublished or password-protected). Short-lived.
 */
export async function getEventPreviewToken(eventId: string): Promise<{ token: string; expiresIn: number }> {
  const response = await apiClient.post<{ token: string; expiresIn: number }>(
    `/events/${eventId}/preview-token`,
  );
  return response.data;
}

/** Upload a gallery image. Plan limit enforced by backend. */
export async function uploadGalleryImage(
  eventId: string,
  file: File,
): Promise<{ url: string; gallery: string[] }> {
  const formData = new FormData();
  formData.append("image", file);
  try {
    const response = await apiClient.post<{ url: string; gallery: string[] } | { data: { url: string; gallery: string[] } } | ApiErrorResponse>(
      `/events/${eventId}/gallery`,
      formData,
    );
    const data = response.data;
    if ("error" in data) {
      throw new GalleryUploadError((data as ApiErrorResponse).error?.code ?? "validation_failed", (data as ApiErrorResponse).error?.message);
    }
    const payload = (data as { data?: { url: string; gallery: string[] } }).data ?? (data as { url: string; gallery: string[] });
    return payload;
  } catch (error) {
    if (error instanceof GalleryUploadError) throw error;
    throw toGalleryUploadError(error);
  }
}

/** Delete a gallery image by index or filename. */
export async function deleteGalleryImage(
  eventId: string,
  filenameOrIndex: string | number,
): Promise<void> {
  await apiClient.delete(`/events/${eventId}/gallery/${encodeURIComponent(String(filenameOrIndex))}`);
}

/** Upload hero image (gallery[0]). Available for all plans — no plan gating. */
export async function uploadHeroImage(
  eventId: string,
  file: File,
): Promise<{ url: string; gallery: string[] }> {
  const formData = new FormData();
  formData.append("image", file);
  try {
    const response = await apiClient.post<{ url: string; gallery: string[] } | { data: { url: string; gallery: string[] } } | ApiErrorResponse>(
      `/events/${eventId}/hero-image`,
      formData,
    );
    const data = response.data;
    if ("error" in data) {
      throw new GalleryUploadError((data as ApiErrorResponse).error?.code ?? "validation_failed", (data as ApiErrorResponse).error?.message);
    }
    const payload = (data as { data?: { url: string; gallery: string[] } }).data ?? (data as { url: string; gallery: string[] });
    return payload;
  } catch (error) {
    if (error instanceof GalleryUploadError) throw error;
    throw toGalleryUploadError(error);
  }
}

/**
 * JUB-39/JUB-144: upload or replace a gift's (or cash fund's) image. Resolves
 * with the image URL to show (served by the event's gated media route).
 */
export async function uploadGiftImage(eventUuid: string, giftUuid: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append("image", file);
  try {
    const response = await apiClient.post<{ data?: { imageUrl?: string } }>(
      API_ROUTES_RELATIVE.gifts.image(eventUuid, giftUuid),
      formData,
    );
    const url = response.data?.data?.imageUrl;
    if (!url) {
      throw new GalleryUploadError("file_upload_failed");
    }
    return url;
  } catch (error) {
    if (error instanceof GalleryUploadError) throw error;
    throw toGalleryUploadError(error);
  }
}

/** JUB-39/JUB-144: remove a gift's image (the file is deleted server-side). */
export async function deleteGiftImage(eventUuid: string, giftUuid: string): Promise<void> {
  try {
    await apiClient.delete(API_ROUTES_RELATIVE.gifts.image(eventUuid, giftUuid));
  } catch (error) {
    throw toGalleryUploadError(error);
  }
}

/** JUB-144: upload or replace the event's custom cover image. */
export async function uploadEventCover(eventUuid: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append("image", file);
  try {
    const response = await apiClient.post<{ data?: { coverUrl?: string } }>(
      API_ROUTES_RELATIVE.events.cover(eventUuid),
      formData,
    );
    const url = response.data?.data?.coverUrl;
    if (!url) {
      throw new GalleryUploadError("file_upload_failed");
    }
    return url;
  } catch (error) {
    if (error instanceof GalleryUploadError) throw error;
    throw toGalleryUploadError(error);
  }
}

/** JUB-144: remove the event's custom cover image. */
export async function deleteEventCover(eventUuid: string): Promise<void> {
  try {
    await apiClient.delete(API_ROUTES_RELATIVE.events.cover(eventUuid));
  } catch (error) {
    throw toGalleryUploadError(error);
  }
}

/** Delete hero image (gallery[0]). Available for all plans. */
export async function deleteHeroImage(eventId: string): Promise<void> {
  await apiClient.delete(`/events/${eventId}/hero-image`);
}

export type EventRsvpSettings = {
  rsvpEnabled: boolean;
  kidsEnabled: boolean;
  accommodationEnabled: boolean;
  emailNotifications: boolean;
  dueDate?: string | null;
  mealOptions: {
    standard: boolean;
    vegan: boolean;
    vegetarian: boolean;
  };
};

export type EventRsvpSettingsPayload = Partial<EventRsvpSettings>;

export async function getEventRsvpSettings(eventId: string): Promise<EventRsvpSettings> {
  const response = await apiClient.get<EventRsvpSettings | ApiErrorResponse>(
    `/events/${eventId}/rsvp-settings`,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to load RSVP settings");
  }

  return data as EventRsvpSettings;
}

export async function updateEventRsvpSettings(
  eventId: string,
  payload: EventRsvpSettingsPayload,
): Promise<EventRsvpSettings> {
  const response = await apiClient.put<EventRsvpSettings | ApiErrorResponse>(
    `/events/${eventId}/rsvp-settings`,
    payload,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to update RSVP settings");
  }

  return data as EventRsvpSettings;
}

// RSVP and Guest types
export type Guest = {
  id: string;
  rsvpId: string;
  name: string;
  type: "adult" | "kid";
  menuPreference?: "standard" | "vegetarian" | "vegan";
  accommodationNeeded?: boolean;
  tableName?: string | null;
  ceremonyAttendance?: {
    civil?: boolean;
    religious?: boolean;
    baptism?: boolean;
    party?: boolean;
  };
};

export type RSVP = {
  id: string;
  eventId: string;
  email: string | null; // JUB-85: organizer-created RSVPs may have no email
  phone?: string;
  familyName?: string;
  tableName?: string | null;
  status: "confirmed" | "pending" | "declined";
  submittedAt?: string;
  notes?: string | null; // JUB-58: guest's free-text message / dietary note (per RSVP group)
  guests: Guest[];
};

export async function getEventGuests(eventUuid: string): Promise<RSVP[]> {
  // Add cache-busting timestamp to prevent stale data
  const timestamp = Date.now();
  const response = await apiClient.get<RSVP[] | { data: RSVP[]; message_key?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/guests`,
    {
      params: { _t: timestamp },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    }
  );
  
  const data = response.data;
  if ("error" in data) {
    console.error("API returned error:", data.error);
    throw new Error(data.error?.message || "Failed to load guests");
  }
  
  // Unwrap if response is wrapped in { data: ..., message_key: ... }
  let rsvps: RSVP[];
  
  // Check if response has the standard API wrapper structure
  if (data && typeof data === "object" && !Array.isArray(data)) {
    // Check for both possible wrapper formats
    if ("data" in data && "message_key" in data) {
      // Standard API response: { message_key: "...", data: [...] }
      const wrappedData = (data as { data: unknown; message_key?: string }).data;
      if (Array.isArray(wrappedData)) {
        rsvps = wrappedData as RSVP[];
      } else {
        console.error("data.data is not an array:", wrappedData);
        rsvps = [];
      }
    } else if ("data" in data && Array.isArray((data as { data: unknown }).data)) {
      // Alternative wrapper: { data: [...] }
      rsvps = (data as { data: RSVP[] }).data;
    } else if (Array.isArray(data)) {
      // Direct array response
      rsvps = data as RSVP[];
    } else {
      console.error("Unexpected response format:", data);
      console.error("Response keys:", Object.keys(data));
      rsvps = [];
    }
  } else if (Array.isArray(data)) {
    rsvps = data as RSVP[];
  } else {
    console.error("Unexpected response format:", data);
    console.error("Response type:", typeof data);
    rsvps = [];
  }
  if (rsvps.length === 0) {
    console.warn("No RSVPs returned from API!");
  }
  return rsvps;
}

export type CreateRSVPPayload = {
  email?: string; // JUB-85: optional on the owner-side manual-add path
  phone?: string;
  familyName?: string;
  tableName?: string | null;
  notes?: string | null;
  status: "confirmed" | "pending" | "declined";
  guests: CreateGuestPayload[];
};

export type CreateGuestPayload = {
  name: string;
  type: "adult" | "kid";
  menuPreference?: "standard" | "vegetarian" | "vegan";
  accommodationNeeded?: boolean;
  tableName?: string | null;
  ceremonyAttendance?: {
    civil?: boolean;
    religious?: boolean;
    baptism?: boolean;
    party?: boolean;
  };
};

export type UpdateRSVPPayload = Partial<Omit<CreateRSVPPayload, "guests">> & {
  guests?: (CreateGuestPayload & { id?: string })[];
};

export async function createRSVP(eventUuid: string, payload: CreateRSVPPayload): Promise<RSVP> {
  const response = await apiClient.post<RSVP | { data: RSVP; message_key?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/guests`,
    payload
  );
  
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to create RSVP");
  }
  
  // Unwrap if response is wrapped in { data: ..., message_key: ... }
  let rsvp: RSVP;
  if (data && typeof data === "object" && "data" in data && !Array.isArray(data)) {
    // Response is wrapped: { data: RSVP, message_key: "..." }
    const unwrappedData = (data as { data: unknown; message_key?: string }).data;
    if (unwrappedData && typeof unwrappedData === "object" && "id" in unwrappedData && "email" in unwrappedData) {
      rsvp = unwrappedData as RSVP;
    } else {
      console.error("Unwrapped data is not a valid RSVP:", unwrappedData);
      throw new Error("Invalid response format: data field does not contain a valid RSVP");
    }
  } else if (data && typeof data === "object" && "id" in data && "email" in data) {
    // Direct RSVP object (no wrapping)
    rsvp = data as RSVP;
  } else {
    console.error("Unexpected response format:", data);
    console.error("Response structure:", JSON.stringify(data, null, 2));
    throw new Error("Invalid response format from create RSVP API. Expected RSVP object or { data: RSVP }");
  }
  return rsvp;
}

export async function updateRSVP(eventUuid: string, guestId: string, payload: UpdateRSVPPayload): Promise<RSVP> {
  const response = await apiClient.put<RSVP | { data: RSVP; message_key?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/guests/${guestId}`,
    payload
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to update RSVP");
  }
  // Unwrap if response is wrapped in { data: ... }
  if (data && typeof data === "object" && "data" in data && data.data) {
    return data.data as RSVP;
  }
  return data as RSVP;
}

export async function deleteRSVP(eventUuid: string, guestId: string): Promise<void> {
  const response = await apiClient.delete<{ message?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/guests/${guestId}`
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to delete RSVP");
  }
}

export async function addGuestToRSVP(eventUuid: string, rsvpId: string, payload: CreateGuestPayload): Promise<Guest> {
  const response = await apiClient.post<RSVP | { data: RSVP; message_key?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/guests`,
    {
      ...payload,
      rsvpId,
    }
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to add guest");
  }
  // Unwrap if response is wrapped in { data: ... }
  const rsvpData = (data && typeof data === "object" && "data" in data && data.data) ? data.data : data;
  // The response is an RSVP, we need to find the newly added guest
  const rsvp = rsvpData as RSVP;
  // Return the last guest (the newly added one)
  if (Array.isArray(rsvp.guests) && rsvp.guests.length > 0) {
    return rsvp.guests[rsvp.guests.length - 1];
  }
  throw new Error("Failed to add guest: guest not found in response");
}

export async function updateGuest(eventUuid: string, guestId: string, payload: Partial<CreateGuestPayload>): Promise<Guest> {
  const response = await apiClient.put<Guest | ApiErrorResponse>(
    `/events/${eventUuid}/guests/${guestId}`,
    payload
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to update guest");
  }
  return data as Guest;
}

export async function deleteGuest(eventUuid: string, guestId: string): Promise<void> {
  const response = await apiClient.delete<{ message?: string } | ApiErrorResponse>(
    `/events/${eventUuid}/guests/${guestId}`
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to delete guest");
  }
}

export type GuestsCsvExport = {
  /** UTF-8 CSV (with BOM) as served by the backend. */
  blob: Blob;
  /** Filename from Content-Disposition, e.g. "nunta-ana-si-mihai-invitati.csv". */
  filename: string;
};

/** Filename from a Content-Disposition header, or null when absent/unparseable. */
export function parseContentDispositionFilename(header: string | undefined | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      /* fall through to the plain filename */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/**
 * JUB-89: fetch the guest list as CSV through the authenticated client (the
 * bearer token travels with the request, so a plain <a href> would not work).
 * `lang` selects the header/label language of the sheet (ro | en).
 */
export async function exportEventGuestsCsv(eventUuid: string, lang: "ro" | "en" = "ro"): Promise<GuestsCsvExport> {
  const response = await apiClient.get<Blob>(`/events/${eventUuid}/guests/export`, {
    params: { lang },
    responseType: "blob",
    headers: { Accept: "text/csv" },
  });
  const disposition = response.headers?.["content-disposition"] as string | undefined;
  return {
    blob: response.data,
    filename: parseContentDispositionFilename(disposition) ?? `${eventUuid}-invitati.csv`,
  };
}

/**
 * JUB-225: bulk-import guests from a CSV file via multipart/form-data.
 */
export async function importEventGuestsCsv(
  eventUuid: string,
  file: File
): Promise<{ imported: number }> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post<
    { imported: number; data?: { imported: number } } | ApiErrorResponse
  >(`/events/${eventUuid}/guests/import`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to import guests");
  }
  const imported =
    typeof data.imported === "number"
      ? data.imported
      : typeof data.data?.imported === "number"
        ? data.data.imported
        : 0;
  return { imported };
}

/** Hand a Blob to the browser as a file download. */
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

export type GiftFund = {
  /** Public uuid of the fund's gift row; addresses its image (JUB-39). */
  id?: string;
  slug: string;
  name: string;
  description?: string | null;
  thankYou?: string | null;
  goal: number;
  currency: string;
  enabled: boolean;
  cover?: string | null;
  totalContributions: number;
  requiresBankLink: boolean;
};

export type GiftContribution = {
  id: string; // UUID
  guest?: string | null;
  giftName?: string | null;
  amount: number;
  currency: string;
  processor?: string | null;
  status: string;
  createdAt?: string | null;
  refunded?: boolean;
  refundedAmount?: number;
  remainingRefundableAmount?: number;
  refund?: {
    amount: number;
    reason: string;
    processedAt?: string | null;
  } | null;
};

export type GiftRegistryResponse = {
  funds: GiftFund[];
  contributions: GiftContribution[];
  planLocked?: boolean;
  giftEmailNotifications?: boolean;
};

export async function updateGiftRegistrySettings(
  eventId: string,
  payload: { giftEmailNotifications: boolean },
): Promise<{ giftEmailNotifications: boolean }> {
  const response = await apiClient.put<{ giftEmailNotifications: boolean } | { data: { giftEmailNotifications: boolean } } | ApiErrorResponse>(
    `/events/${eventId}/gift-registry/settings`,
    payload,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to update gift registry settings");
  }
  if (data && typeof data === "object" && "data" in data && data.data) {
    return data.data;
  }
  return data as { giftEmailNotifications: boolean };
}

export async function getGiftRegistry(eventId: string): Promise<GiftRegistryResponse> {
  const response = await apiClient.get<GiftRegistryResponse | ApiErrorResponse>(
    `/events/${eventId}/gift-registry`,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to load gift registry");
  }

  return data as GiftRegistryResponse;
}

export type RefundRequest = {
  reason: string;
  amount?: number; // Optional for partial refunds
};

export type RefundResponse = {
  refund: {
    refund_id: number;
    refund_charge_id: number;
    refund_event_id: number;
    refund_user_id: number;
    refund_external_id?: string | null;
    refund_amount: number;
    refund_currency: string;
    refund_reason: string;
    refund_status: string;
    refund_created_at: string;
    refund_processed_at?: string | null;
  };
  charge: {
    charge_id: number;
    charge_status: boolean;
    charge_cancel: boolean;
    // ... other charge fields
  };
};

export async function refundCharge(
  eventUuid: string,
  chargeUuid: string,
  payload: RefundRequest,
): Promise<RefundResponse> {
  const response = await apiClient.post<RefundResponse | ApiErrorResponse>(
    `/events/${eventUuid}/charges/${chargeUuid}/refund`,
    payload,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to process refund");
  }

  return data as RefundResponse;
}

export async function updateGiftFund(
  eventId: string,
  slug: string,
  payload: Partial<Pick<GiftFund, "name" | "description" | "thankYou" | "goal" | "enabled" | "cover">>,
): Promise<GiftFund> {
  const response = await apiClient.put<GiftFund | ApiErrorResponse>(
    `/events/${eventId}/gift-registry/${slug}`,
    payload,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to update gift fund");
  }

  return data as GiftFund;
}

export async function deleteGiftFund(
  eventId: string,
  slug: string,
): Promise<GiftFund> {
  const response = await apiClient.delete<GiftFund | { data: GiftFund } | ApiErrorResponse>(
    `/events/${eventId}/gift-registry/${slug}`,
  );
  const data = response.data;
  if ("error" in data) {
    throw new Error(data.error?.message || "Failed to delete gift fund");
  }
  if (data && typeof data === "object" && "data" in data && data.data) {
    return data.data;
  }
  return data as GiftFund;
}

